import React, { useEffect, useState, useRef, useMemo } from 'react';
import { GenericModal } from 'src/components/Modals/GenericModal';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { CloseIcon } from 'src/components/SvgIcons/SvgIcons';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Divider } from '@mui/material';
import color from 'src/utils/colors';
import { ConfirmationDialog } from './ConfirmationDialog';
import { StyledSettingInput } from './StyledSettingInput';
import { ControlledDropdownMenu } from './StyledDropdown';
import {
  BRAND_NAME_FORMATTING,
  APPROVED_SUBSTITUTIONS,
  AVOID_CROSS_CATEGORY_COMPARISONS,
  APPROVED_NUTRIENT_RELATED_PHRASES,
  AVOID_SPECIFIC_CLAIM_LANGUAGE,
  APPROVED_ON_PACKAGE_LANGUAGE,
  APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS,
  APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT,
  AVOID_RESTRICTED_TEXT_FOR_CLAIMS
} from '../types/profileTypes';
import { useFormContext, useWatch, FormProvider, useForm, UseFormReturn, FieldValues, Path } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ModalSection from './ModalSection';
import { useRequirementsModalStore } from '../stores/requirementsModalStore';
import { useFormConnection } from '../hooks/useFormConnection';
import { ModalFormSchema, ModalFormValuesTypes } from '../schemas/modalFormSchema';
import { useDefaultParameters } from '../hooks/useDefaultParameters';

const GuidelineTypesValues = {
  [BRAND_NAME_FORMATTING]: {
    label: 'Brand Name Formatting',
    description: 'Guides the automation tool on how to format a brand name.'
  },
  [AVOID_CROSS_CATEGORY_COMPARISONS]: {
    label: 'Avoid Cross Category or Subcategory Comparisons',
    description: 'Guide the automation tool to avoid making claims against products in a different cat or subcat.'
  },
  [APPROVED_SUBSTITUTIONS]: {
    label: 'Approved Substitutions for Restricted Text',
    description: 'Guide the automation tool to use approved text in place of restricted text.'
  },
  [APPROVED_NUTRIENT_RELATED_PHRASES]: {
    label: 'Approved Nutrient Related Phrases',
    description: 'Guide the automation tool on approved phrases to use when referring to product nutrients.'
  },
  [AVOID_SPECIFIC_CLAIM_LANGUAGE]: {
    label: 'Avoid Specific Claim Language',
    description: 'Guide the automation tool to avoid making specific claims about the product.'
  },
  [APPROVED_ON_PACKAGE_LANGUAGE]: {
    label: 'Approved On-Package Language for Product Claims',
    description: 'Guide the automation tool to only use approved on-package claims.'
  },
  [APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS]: {
    label: 'Approved Qualifier Phrases Before Product Claims',
    description: 'Guide the automation tool to leverage qualifiers before a claim about the product.'
  },
  [APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT]: {
    label: 'Approved Product Attribute',
    description: 'Guide the automation tool to use approved language before a disclaimer for product attributes.'
  },
  [AVOID_RESTRICTED_TEXT_FOR_CLAIMS]: {
    label: 'Avoid Restricted Text for Product Claims',
    description: 'Guide the automation tool to avoid restricted text when making specific claims about the product.'
  }
};

const ModalContent = <T extends FieldValues>({
  onSave,
  mainForm,
  onDeleteClick
}: {
  onSave: (values: ModalFormValuesTypes, index: number) => void;
  mainForm: UseFormReturn<T>;
  onDeleteClick: () => void;
}) => {
  const { index: modalIndex, closeModal: handleModalClose } = useRequirementsModalStore();
  const modalForm = useFormContext<ModalFormValuesTypes>();
  const defaultsSetForTypeRef = useRef<string | undefined>(undefined);

  // Get the current guideline from the main form
  const currentGuideline = mainForm.watch(`settings.guidelines.${modalIndex}` as Path<T>);

  // Watch the type field to determine which default values to use
  const type = useWatch({
    control: modalForm.control,
    name: 'type'
  });

  // Watch name field for clear button state
  const name = useWatch({
    control: modalForm.control,
    name: 'name'
  });

  const defaultParameters = useDefaultParameters(type);

  // Clear button should be enabled if name has value or type has been selected
  const isClearEnabled = !!name || !!type;

  useEffect(() => {
    // Only set defaults when type is selected and we haven't already set defaults for this type
    if (type && !currentGuideline && defaultsSetForTypeRef.current !== type) {
      modalForm.setValue('parameters', defaultParameters.parameters, { shouldValidate: true });
      defaultsSetForTypeRef.current = type;
    }
  }, [type, modalForm, defaultParameters, currentGuideline]);

  // Reset ref when modal closes or currentGuideline changes
  useEffect(() => {
    if (currentGuideline) {
      defaultsSetForTypeRef.current = currentGuideline.type;
    } else if (!type) {
      defaultsSetForTypeRef.current = undefined;
    }
  }, [currentGuideline, type]);

  const handleClose = () => {
    handleModalClose();
    modalForm.reset();
  };

  const handleClear = () => {
    modalForm.reset({
      name: '',
      type: undefined,
      parameters: {}
    });
    defaultsSetForTypeRef.current = undefined;
  };

  const handleSave = () => {
    const values = modalForm.getValues();
    onSave(values, modalIndex);
    handleClose();
  };

  return (
    <>
      <Flex alignItems="center" className="genericModal__header">
        {currentGuideline && (
          <SlButton
            buttonSx={{
              border: 'unset',
              padding: 0,
              fontWeight: 500,
              fontSize: '14px',
              width: 'fit-content',
              minWidth: 'unset',
              marginLeft: '24px',
              minHeight: 'unset',
              height: 'fit-content',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
            textSx={{
              color: color.projectedSpend
            }}
            onClick={onDeleteClick}
          >
            Delete
          </SlButton>
        )}
        <Text
          variant="h4"
          sx={{
            marginLeft: 'auto',
            marginRight: '249px'
          }}
        >
          {currentGuideline ? 'Edit Requirement' : 'Add Requirement'}
        </Text>
        <CloseIcon
          style={{ cursor: 'pointer', width: '24px', height: '24px', marginBottom: 'auto' }}
          onClick={handleClose}
        />
      </Flex>
      <Divider
        sx={{
          marginTop: '22px',
          position: 'relative',
          left: '-100px',
          width: '1000px'
        }}
      />
      <GenericModal.Body>
        <Text variant="h7" fontWeight={500}>
          Requirement {modalIndex + 1}
        </Text>
        <Flex flexDirection="column">
          <StyledSettingInput
            label="Title"
            placeholder="Name your requirement"
            characterLimit={50}
            name={`name`}
            textSx={{
              fontWeight: 500
            }}
          />
          <Flex flexDirection="column" gap={8} marginTop="16px" marginBottom="16px">
            <Text variant="h7" fontWeight={500}>
              Requirement Type
            </Text>
            <ControlledDropdownMenu
              name={`type`}
              defaultLabel="Select the requirement you would like to implement."
              options={Object.entries(GuidelineTypesValues).map(([key, { label, description }]) => ({
                id: key,
                label,
                description
              }))}
              stacklineButtonProps={{
                buttonSx: {
                  width: '100%',
                  justifyContent: 'space-between',
                  '& .MuiTypography-root': {
                    fontWeight: '400 !important',
                    ...(type ? {} : { color: color.blueGrey })
                  }
                }
              }}
              menuStyles={{
                height: '224px',
                border: '1px solid #E8E8ED',
                '& .MuiMenuItem-root': {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  height: '56px',
                  borderBottom: '1px solid #E8E8ED',
                  '&:last-child': {
                    borderBottom: 'none'
                  }
                },
                '&::-webkit-scrollbar': {
                  width: '5px'
                },
                '&::-webkit-scrollbar-thumb': {
                  background: color.blueGrey,
                  borderRadius: '20px'
                }
              }}
            />
          </Flex>
        </Flex>
        <ModalSection index={modalIndex} />
      </GenericModal.Body>
      <GenericModal.Footer>
        <Flex justifyContent="space-between">
          <SlButton onClick={currentGuideline ? handleClose : handleClear} disabled={currentGuideline ? false : !isClearEnabled}>
            {currentGuideline ? 'Cancel' : 'Clear'}
          </SlButton>
          <SlButton variant="contained" onClick={handleSave} disabled={!modalForm.formState.isValid}>
            Save
          </SlButton>
        </Flex>
      </GenericModal.Footer>
    </>
  );
};

/**
 * T is parent form type, which could be either FinalFormSchema or BaseSettingsFormSchema
 * @returns
 */
export function RequirementsModal<T extends FieldValues>() {
  const { isOpen, closeModal, index: modalIndex } = useRequirementsModalStore();
  const { updateMainFormAtIndex, removeItemAtIndex } = useFormConnection();
  const mainForm = useFormContext<T>();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const form = useForm<ModalFormValuesTypes>({
    resolver: zodResolver(ModalFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      type: undefined,
      parameters: {}
    }
  });

  const currentGuideline = mainForm.watch(`settings.guidelines.${modalIndex}` as Path<T>);

  useEffect(() => {
    if (currentGuideline) {
      form.reset(currentGuideline);
    } else {
      form.reset({
        name: '',
        type: undefined,
        parameters: {}
      });
    }
  }, [currentGuideline, form]);

  const handleSave = (values: ModalFormValuesTypes, index: number) => {
    updateMainFormAtIndex(index, values);
  };

  const handleDeleteClick = () => {
    // Store the index and close the edit modal first
    setDeleteIndex(modalIndex);
    closeModal();
    form.reset();
    // Open the delete confirmation dialog
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (deleteIndex !== null) {
      removeItemAtIndex(deleteIndex);
    }
    setIsDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setDeleteIndex(null);
  };

  return (
    <>
      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Profile Requirement"
        message="Are you sure you want to delete this requirement? Once this requirement is deleted, it cannot be recovered."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
      <GenericModal
        open={isOpen}
        onClose={() => {
          closeModal();
        }}
        sx={{
          '& .MuiPaper-root': {
            width: '752px',
            height: '641px',
            padding: '24px'
          },
          '& .genericModal__footer': {
            padding: '20px 48px'
          },
          '& .genericModal__body': {
            gap: '16px',
            overflowY: 'auto',
            padding: '24px 24px',
            height: 'calc(100% - 97px)',
            scrollbarGutter: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px'
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent'
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.3)'
              }
            }
          }
        }}
      >
        <FormProvider {...form}>
          <ModalContent onSave={handleSave} mainForm={mainForm} onDeleteClick={handleDeleteClick} />
        </FormProvider>
      </GenericModal>
    </>
  );
}
